import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import { shallowEqual, useSelector } from 'umi';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { CountryRegionOptionsContext } from './PerformanceFilter';

export const NET_DELAY_API =
  '/api/v1/dashboard/perfsight2/openapi/net/netDelayData';
export { NET_DELAY_API as api };

const DEFAULT_DATA_KEY = 'networkLatency';
const DEFAULT_TIMEZONE_OFFSET = 0;
const DEFAULT_TODAY_LOOKBACK_HOURS = 11;
const EMPTY_COUNTRY_REGION_OPTIONS: any[] = [];
const EMPTY_REQUEST_PARAMS: Record<string, any> = {};

const resolveFormatter = (value: any, fallback: (input: any) => any) => {
  if (typeof value === 'string' && value) {
    return stringToFunction(value);
  }
  if (typeof value === 'function') {
    return value;
  }
  return fallback;
};

const unwrapCountryValue = (value: any): any => {
  if (value === undefined || value === null || value === '') {
    return value;
  }
  if (Array.isArray(value)) {
    return unwrapCountryValue(value[0]);
  }
  if (typeof value === 'object') {
    if (value.country_code !== undefined && value.country_code !== null) {
      return value.country_code;
    }
    if (value.value !== undefined && value.value !== null) {
      return value.value;
    }
  }
  return value;
};

const getCountryRegionOption = (
  country: any,
  countryRegionOptions: any[] = [],
) => {
  const countryValue = unwrapCountryValue(country);
  if (
    countryValue === undefined ||
    countryValue === null ||
    countryValue === ''
  ) {
    return undefined;
  }
  return countryRegionOptions.find(
    (item: any) => Number(item?.country_code) === Number(countryValue),
  );
};

const isRegionOption = (option: any) => {
  const name = String(
    option?.country_name_ch || option?.country_name_en || '',
  );
  return name.includes('地区');
};

const normalizeDateStr = (value: any): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (moment.isMoment(value)) {
    return value.format('YYYY-MM-DD');
  }
  const str = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : '';
};

/** 今天：上一完整 UTC 整点往前 N 小时（不含当前小时） */
const getTodayLookbackRange = (lookbackHours: number) => {
  const end = moment.utc().startOf('hour').subtract(1, 'hour');
  const start = end.clone().subtract(lookbackHours, 'hours');
  return {
    start: start.format('YYYY-MM-DD HH:mm:ss'),
    end: end.format('YYYY-MM-DD HH:mm:ss'),
  };
};

/** 按筛选器日期的本地整天传参，不做 UTC 偏移 */
const getFilterDateLocalRange = (startDate: string, endDate: string) => ({
  start: startDate ? `${startDate} 00:00:00` : '',
  end: endDate ? `${endDate} 23:59:59` : '',
});

const getTodayDateStr = (timezoneOffset: number): string =>
  moment().utcOffset(timezoneOffset).format('YYYY-MM-DD');

const isTodayRange = (
  startDate: string,
  endDate: string,
  timezoneOffset: number,
): boolean => {
  const today = getTodayDateStr(timezoneOffset);
  return Boolean(startDate && startDate === endDate && startDate === today);
};

const getTimezoneOffset = (requestParams: any): number => {
  const timezoneOffset = Number(requestParams?.timezoneOffset);
  return Number.isFinite(timezoneOffset)
    ? timezoneOffset
    : DEFAULT_TIMEZONE_OFFSET;
};

export const formatBeforeRequest = (data: any = {}) => {
  const filterValue = data.filterVal || data.filterValue;
  const countryRegionOptions = data.countryRegionOptions || [];
  const gameCode = data.gameCode;
  const requestParams = data.requestParams || {};
  const {
    convertTodayToUtc,
    todayLookbackHours,
    ...restRequestParams
  } = requestParams;
  const rangeTime = filterValue?.rangeTime || filterValue?.rangeDaily || [];
  const countryValue = unwrapCountryValue(filterValue?.country);
  const isAllCountry =
    countryValue === undefined ||
    countryValue === null ||
    countryValue === '' ||
    Number(countryValue) === -1;
  const countryRegionOption = getCountryRegionOption(
    countryValue,
    countryRegionOptions,
  );
  const countryName = isAllCountry
    ? '全部'
    : countryRegionOption?.country_name_en ||
      countryRegionOption?.country_name_ch ||
      '';
  const cityValue =
    isAllCountry || isRegionOption(countryRegionOption)
      ? []
      : countryRegionOption?.request_city ?? [];
  const startDate = normalizeDateStr(rangeTime?.[0]);
  const endDate = normalizeDateStr(rangeTime?.[1]);
  const timezoneOffset = getTimezoneOffset(requestParams);
  const lookbackHours = Number(todayLookbackHours);
  const todayLookback =
    Number.isFinite(lookbackHours) && lookbackHours > 0
      ? lookbackHours
      : DEFAULT_TODAY_LOOKBACK_HOURS;
  const { start, end } =
    convertTodayToUtc === true &&
    isTodayRange(startDate, endDate, timezoneOffset)
      ? getTodayLookbackRange(todayLookback)
      : getFilterDateLocalRange(startDate, endDate);

  return {
    ...restRequestParams,
    game_code: gameCode,
    start,
    end,
    timezoneOffset,
    _country: [countryName],
    _city: cityValue,
    countryId: isAllCountry
      ? -1
      : Number(countryRegionOption?.country_code ?? countryValue ?? -1),
    cityIds: isAllCountry ? [] : countryRegionOption?.cityIds || [],
    AggType:
      isAllCountry || isRegionOption(countryRegionOption)
        ? requestParams.AggType || 'country'
        : 'province',
    ...(isAllCountry
      ? { IsCityAll: true, _province: [] }
      : { _province: requestParams._province ?? ['全部'] }),
  };
};

const getPayload = (source: any) =>
  source?.data?.ret || source?.ret || source?.data || source || {};

const NETWORK_TYPE_BY_TREND_KEY: Array<{
  networkType: number;
  valueKey: string;
  countKey: string;
}> = [
    { networkType: 1, valueKey: 'dataWifi', countKey: 'battleDataWifi' },
    { networkType: 2, valueKey: 'data2g', countKey: 'battleData2g' },
    { networkType: 3, valueKey: 'data3g', countKey: 'battleData3g' },
    { networkType: 4, valueKey: 'data4g', countKey: 'battleData4g' },
    { networkType: 5, valueKey: 'data5g', countKey: 'battleData5g' },
  ];

const normalizeNetworkType = (item: any) => ({
  ...item,
  NetworkType: Number(item?.NetworkType) === 6 ? 5 : item?.NetworkType,
});

const flattenHoursToTable = (hoursData: any, times: string[] = []) => {
  const orderedTimes = times.length
    ? [...times].reverse()
    : Object.keys(hoursData || {}).reverse();
  return orderedTimes.reduce((rows: any[], time: string) => {
    const items = hoursData?.[time];
    if (Array.isArray(items) && items.length) {
      rows.push(...items);
    }
    return rows;
  }, []);
};

const buildHoursFromTrend = (source: any) => {
  const trend = source?.netDelayTrend || {};
  const netDataTimes =
    (Array.isArray(trend?.xData) && trend.xData.length
      ? trend.xData
      : source?.netDelayTime) || [];
  const netHoursData = netDataTimes.reduce(
    (result: any, time: string, index: number) => ({
      ...result,
      [time]: [
        ...NETWORK_TYPE_BY_TREND_KEY.map(({ networkType, valueKey, countKey }) => ({
          CalendarTime: time,
          NetworkType: networkType,
          Value: trend?.[valueKey]?.[index],
          MergeCount: trend?.[countKey]?.[index],
        })),
        {
          CalendarTime: time,
          NetworkType: 0,
          Value: trend?.dataCommon?.[index],
          MergeCount: trend?.count?.[index],
        },
      ],
    }),
    {},
  );
  return { netDataTimes, netHoursData };
};

const hasZoneMetric = (item: any): boolean =>
  Number(item?.MergeCount) > 0 ||
  Number(item?.Value) > 0 ||
  Number(item?.CountRatio) > 0;

/** 接口可能返回 { 印度: [...] } 或带 children 的国家节点 */
export const flattenZoneAgg = (zoneRows: any): any[] => {
  if (Array.isArray(zoneRows)) {
    return zoneRows.reduce((rows: any[], item: any) => {
      const children = item?.children || item?.list || item?.data;
      if (Array.isArray(children) && children.length) {
        children.forEach((child: any) => {
          rows.push({
            ...item,
            ...child,
            CountryName:
              child?.CountryName || item?.CountryName || item?.name,
            CountryNameEn: child?.CountryNameEn || item?.CountryNameEn,
            ProvinceName:
              child?.ProvinceName ||
              child?.name ||
              child?.label ||
              item?.ProvinceName,
          });
        });
        return rows;
      }
      rows.push(item);
      return rows;
    }, []);
  }
  if (!zoneRows || typeof zoneRows !== 'object') {
    return [];
  }
  return Object.keys(zoneRows).reduce((rows: any[], key: string) => {
    const list = zoneRows[key];
    if (!Array.isArray(list)) {
      return rows;
    }
    flattenZoneAgg(list).forEach((item: any) => {
      const regionName = String(
        item?.ProvinceName || item?.CityName || item?.name || item?.label || '',
      ).trim();
      rows.push({
        ...item,
        CountryName: item?.CountryName || key,
        ProvinceName:
          item?.ProvinceName ||
          (regionName &&
          regionName !== key &&
          regionName !== String(item?.CountryName || '')
            ? regionName
            : item?.ProvinceName),
      });
    });
    return rows;
  }, []);
};

const isAllCountryFilter = (filterVal?: any): boolean => {
  const countryValue = unwrapCountryValue(filterVal?.country);
  return (
    countryValue === undefined ||
    countryValue === null ||
    countryValue === '' ||
    Number(countryValue) === -1
  );
};

const pickFallbackZoneRows = (
  zoneRows: any,
  ispRows: any[] = [],
  networkAgg: any[] = [],
  networkType: number,
  allowFallback = true,
): any[] => {
  const flattened = flattenZoneAgg(zoneRows);
  if (flattened.length) {
    return flattened;
  }
  if (!allowFallback) {
    return [];
  }
  const ispTotals = (ispRows || []).filter(
    (item) => !String(item?.IspName || '').trim() && hasZoneMetric(item),
  );
  if (ispTotals.length) {
    return ispTotals;
  }
  const normalized = (networkAgg || []).map(normalizeNetworkType);
  if (networkType === 0) {
    const allType = normalized.filter(
      (item) => Number(item?.NetworkType) === 0 && hasZoneMetric(item),
    );
    if (allType.length) {
      return allType;
    }
    const ranked = normalized
      .filter((item) => hasZoneMetric(item))
      .sort((a, b) => (b.CountRatio || 0) - (a.CountRatio || 0));
    return ranked[0] ? [ranked[0]] : [];
  }
  return normalized.filter(
    (item) => Number(item?.NetworkType) === networkType && hasZoneMetric(item),
  );
};

export const formatData = (response: any, filterVal?: any) => {
  const payload = getPayload(response);
  const allowZoneFallback = isAllCountryFilter(filterVal);
  const isNewFormat = Boolean(
    payload?.netHoursData || payload?.networkAgg || payload?.zoneAggAll,
  );
  const { netDataTimes, netHoursData } = isNewFormat
    ? {
      netDataTimes:
        (Array.isArray(payload?.netDataTimes) && payload.netDataTimes.length
          ? payload.netDataTimes
          : Object.keys(payload?.netHoursData || {})) || [],
      netHoursData: Object.keys(payload?.netHoursData || {}).reduce(
        (result: any, time: string) => {
          result[time] = (payload.netHoursData[time] || []).map(
            normalizeNetworkType,
          );
          return result;
        },
        {},
      ),
    }
    : buildHoursFromTrend(payload);

  const networkAgg = (payload?.networkAgg || payload?.netDistribution || []).map(
    normalizeNetworkType,
  );

  return {
    ...payload,
    netDataTimes,
    netHoursData,
    netDelayTable:
      payload?.netDelayTable || flattenHoursToTable(netHoursData, netDataTimes),
    networkAgg,
    zoneAggAll: pickFallbackZoneRows(
      payload?.zoneAggAll || payload?.zoneAll || [],
      payload?.ispAggAll,
      networkAgg,
      0,
      allowZoneFallback,
    ),
    zoneAggWifi: pickFallbackZoneRows(
      payload?.zoneAggWifi || payload?.zoneWifi || [],
      payload?.ispAggWifi,
      networkAgg,
      1,
      allowZoneFallback,
    ),
    zoneAgg5G: pickFallbackZoneRows(
      payload?.zoneAgg5G || payload?.zone5G || [],
      payload?.ispAgg5G,
      networkAgg,
      5,
      allowZoneFallback,
    ),
    zoneAgg4G: pickFallbackZoneRows(
      payload?.zoneAgg4G || payload?.zone4G || [],
      payload?.ispAgg4G,
      networkAgg,
      4,
      allowZoneFallback,
    ),
    zoneAgg3G: pickFallbackZoneRows(
      payload?.zoneAgg3G || payload?.zone3G || [],
      payload?.ispAgg3G,
      networkAgg,
      3,
      allowZoneFallback,
    ),
    zoneAgg2G: pickFallbackZoneRows(
      payload?.zoneAgg2G || payload?.zone2G || [],
      payload?.ispAgg2G,
      networkAgg,
      2,
      allowZoneFallback,
    ),
  };
};

/** 从 Service 下发的 props.data 中取出业务数据与 loading / error */
export const getNetworkLatencyState = (
  propsData: any,
  dataKey = DEFAULT_DATA_KEY,
) => {
  const serviceState = propsData?.[dataKey] || {};
  const error =
    serviceState?.errorType || serviceState?.errorMsg || null;
  return {
    data: serviceState?.data || {},
    loading: !!serviceState?.loading,
    error,
    isError: !!error,
  };
};

const NetworkLatencyService = (props: any) => {
  const {
    style,
    className,
    api = NET_DELAY_API,
    dataKey = DEFAULT_DATA_KEY,
    data: propsData,
    children,
    countryRegionOptions: propsCountryRegionOptions,
    requestParams = EMPTY_REQUEST_PARAMS,
    formatBeforeRequest: formatBeforeRequestProp,
    formatData: formatDataProp,
    ...resetProps
  } = props || {};
  const gameCode = useSelector(
    (state: any) => state?.dashboard?.currentGame?.game_code,
    shallowEqual,
  );
  const contextCountryRegionOptions = useContext(CountryRegionOptionsContext);
  const { filterVal, filterLoading } = useContext(FilterContext) || ({} as any);
  const countryRegionOptions =
    (Array.isArray(propsCountryRegionOptions) &&
    propsCountryRegionOptions.length
      ? propsCountryRegionOptions
      : contextCountryRegionOptions) || EMPTY_COUNTRY_REGION_OPTIONS;
  const rangeTime = filterVal?.rangeTime || filterVal?.rangeDaily || [];
  const filterStart = normalizeDateStr(rangeTime?.[0]);
  const filterEnd = normalizeDateStr(rangeTime?.[1]);
  const filterCountry = filterVal?.country;
  const filterTimeZone = filterVal?.timeZone;
  // Service 把整个 filterVal 当依赖，初始化时对象引用会变多次；只在日期/国家真正变化时才重请求
  const stableFilterVal = useMemo(
    () => ({
      rangeTime: [filterStart, filterEnd],
      rangeDaily: [filterStart, filterEnd],
      country: filterCountry,
      timeZone: filterTimeZone,
    }),
    [filterCountry, filterEnd, filterStart, filterTimeZone],
  );
  const data = useMemo(
    () => ({
      gameCode,
      countryRegionOptions,
      requestParams,
    }),
    [countryRegionOptions, gameCode, requestParams],
  );
  const beforeRequestHandler = resolveFormatter(
    formatBeforeRequestProp,
    formatBeforeRequest,
  );
  const formatDataHandler = resolveFormatter(formatDataProp, formatData);

  return (
    <div className={className} style={style}>
      <Service
        api={(params: any) => {
          if (!params?.start || !params?.end || !params?._country?.[0]) {
            return Promise.resolve({ ret: {} });
          }
          if (typeof api === 'function') {
            return api(params);
          }
          return dashboardRequest({
            url: api || NET_DELAY_API,
            method: 'post',
            data: params,
            ignoreRequestId: false,
          });
        }}
        data={data}
        dataKey={dataKey}
        filterVal={stableFilterVal}
        filterLoading={filterLoading}
        trackData={['gameCode', 'requestParams']}
        beforeRequest={(reqData: any) =>
          beforeRequestHandler({
            ...reqData,
            countryRegionOptions,
            gameCode: reqData?.gameCode || gameCode,
            requestParams: reqData?.requestParams || requestParams,
          })
        }
        formatData={formatDataHandler}
      >
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...resetProps,
            dataKey,
            countryRegionOptions,
          }),
        )}
      </Service>
    </div>
  );
};

export default NetworkLatencyService;
